import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { twMerge } from "tailwind-merge";

import type { MBCUIMessage } from "../types/mbc-chat.types";
import type { MbcUploadBinding } from "./parts/UploadPromptPart";

import { getVisibleText, hasVisibleContent } from "../lib/message-content";
import { AlphaUploadPromptPart } from "./parts/AlphaUploadPromptPart";
import { BrandSelectPart } from "./parts/BrandSelectPart";
import { ChoicesPart } from "./parts/ChoicesPart";
import { ReviewBriefCTA } from "./parts/ReviewBriefCTA";
import { TextPart } from "./parts/TextPart";
import { UploadPromptPart } from "./parts/UploadPromptPart";

type MBCMessageBubbleProps = {
  isAlpha?: boolean;
  isInteractiveDisabled: boolean;
  isLastAssistantMessage: boolean;
  isStreaming: boolean;
  isUploading: boolean;
  message: MBCUIMessage;
  onReview: () => void;
  onSelectChoice: (label: string) => void;
  shouldAnimateEntrance?: boolean;
  upload?: MbcUploadBinding;
};

export function MBCMessageBubble({
  isAlpha = false,
  isInteractiveDisabled,
  isLastAssistantMessage,
  isStreaming,
  isUploading,
  message,
  onReview,
  onSelectChoice,
  shouldAnimateEntrance = false,
  upload,
}: MBCMessageBubbleProps) {
  const isUser = message.role === "user";

  const visibleText = getVisibleText(message);

  if (isUser && !visibleText) return null;

  const isSettledLastMessage = isLastAssistantMessage && !isStreaming;

  if (!isUser && !hasVisibleContent(message)) return null;

  if (isLastAssistantMessage && isStreaming && !visibleText) return null;

  // The upload block is the one part that renders outside the bubble: it is a
  // full-width panel in the design, not something that reads as speech.
  // `.at(-1)`: if the agent called the tool twice in one turn, both parts are
  // on the message — the newest one is the one that means anything.
  // In Alpha the block stays on older messages: it edits the live brief, so
  // the customer can scroll back to add or remove files.
  const uploadPrompt = (
    !isUser &&
    (isAlpha ? !isLastAssistantMessage || !isStreaming : isSettledLastMessage)
      ? message.parts.flatMap((part) =>
          part.type === "data-uploadPrompt" ? [part.data] : [],
        )
      : []
  ).at(-1);

  const showDataParts = !isUser && isSettledLastMessage;

  return (
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      className="flex w-full flex-col gap-md"
      initial={shouldAnimateEntrance ? { opacity: 0, y: 6 } : false}
      transition={{ duration: 0.18 }}
    >
      <div
        className={twMerge(
          "flex w-full gap-md",
          isUser ? "justify-end" : "justify-start",
        )}
      >
        {isUser ? null : (
          <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-utility-brand-100 text-fg-brand-primary">
            <Sparkles className="size-4" />
          </div>
        )}

        <div
          className={twMerge(
            isUser
              ? "min-w-0 max-w-[85%] rounded-2xl bg-utility-brand-700 px-lg py-md text-white [overflow-wrap:anywhere]"
              : "min-w-0 max-w-[720px] flex-1 pt-xs text-text-primary [overflow-wrap:anywhere]",
          )}
        >
          {visibleText ? <TextPart text={visibleText} /> : null}

          {showDataParts ? (
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 6 }}
              transition={{ duration: 0.2 }}
            >
              {message.parts.map((part, index) => {
                if (part.type === "data-choices") {
                  return (
                    <ChoicesPart
                      choices={part.data}
                      isDisabled={isInteractiveDisabled}
                      key={`choices-${index}`}
                      onSelect={onSelectChoice}
                    />
                  );
                }

                if (part.type === "data-brandSelect") {
                  return (
                    <BrandSelectPart
                      brandSelect={part.data}
                      isDisabled={isInteractiveDisabled}
                      key={`brand-${index}`}
                      onSelect={onSelectChoice}
                    />
                  );
                }

                if (part.type === "data-reviewCta" && !isAlpha) {
                  return (
                    <ReviewBriefCTA
                      cta={part.data}
                      isDisabled={isInteractiveDisabled || isUploading}
                      key={`review-${index}`}
                      onReview={onReview}
                    />
                  );
                }

                return null;
              })}
            </motion.div>
          ) : null}
        </div>
      </div>

      {uploadPrompt ? (
        <motion.div
          animate={{ opacity: 1, y: 0 }}
          initial={{ opacity: 0, y: 6 }}
          transition={{ duration: 0.2 }}
        >
          {isAlpha ? (
            <AlphaUploadPromptPart
              isDisabled={isInteractiveDisabled}
              onSelectChoice={onSelectChoice}
              prompt={uploadPrompt}
              showActions={isSettledLastMessage}
              upload={upload}
            />
          ) : (
            <UploadPromptPart
              isDisabled={isInteractiveDisabled}
              onSelectChoice={onSelectChoice}
              prompt={uploadPrompt}
              upload={upload}
            />
          )}
        </motion.div>
      ) : null}
    </motion.div>
  );
}
